import { CorruptionInfo } from "@wptb/dev/types";

/**
 * For some reason, the recovery UI from the UI package is not working in the Gutenberg editor.
 * This is a temporary component to display the recovery UI in the Gutenberg editor.
 * It will be replaced with the recovery UI from the UI package when it is ready.
 */

interface RecoveryProps {
  corruption: CorruptionInfo;
  onRecover: () => void;
  onCancel: () => void;
}

export const Recovery = ({ corruption, onRecover, onCancel }: RecoveryProps) => {
  const { expectedCols, expectedRows, rowIssues, missingCells, overflowingSpans } = corruption;

  return (
    <div className="wptb-recovery">
      <div className="wptb-recovery-container">
        <div className="wptb-recovery-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        
        <h2 className="wptb-recovery-title">Table Structure Issue Detected</h2>
        
        <p className="wptb-recovery-description">
          The table has structural inconsistencies that need to be fixed before editing.
          This is usually caused by invalid row/column spans or missing cells.
        </p>

        <div className="wptb-recovery-details">
          <div className="wptb-recovery-stat">
            <span className="wptb-recovery-stat-label">Expected Grid</span>
            <span className="wptb-recovery-stat-value">{expectedRows} rows × {expectedCols} columns</span>
          </div>

          {missingCells.length > 0 && (
            <div className="wptb-recovery-issue">
              <div className="wptb-recovery-issue-header">
                <span className="wptb-recovery-issue-icon wptb-recovery-issue-icon--missing">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" strokeDasharray="4 2" />
                  </svg>
                </span>
                <span className="wptb-recovery-issue-title">
                  {missingCells.length} Missing Cell{missingCells.length > 1 ? 's' : ''}
                </span>
              </div>
              <p className="wptb-recovery-issue-description">
                Empty cells will be inserted at positions: {
                  missingCells.slice(0, 5).map(c => `(${c.row + 1}, ${c.col + 1})`).join(', ')
                }{missingCells.length > 5 ? ` and ${missingCells.length - 5} more...` : ''}
              </p>
            </div>
          )}

          {overflowingSpans.length > 0 && (
            <div className="wptb-recovery-issue">
              <div className="wptb-recovery-issue-header">
                <span className="wptb-recovery-issue-icon wptb-recovery-issue-icon--overflow">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16v16H4zM9 9h10M9 9v10" />
                  </svg>
                </span>
                <span className="wptb-recovery-issue-title">
                  {overflowingSpans.length} Overflowing Span{overflowingSpans.length > 1 ? 's' : ''}
                </span>
              </div>
              <p className="wptb-recovery-issue-description">
                Cell spans extending beyond table boundaries will be reduced.
              </p>
            </div>
          )}

          {rowIssues.length > 0 && (
            <div className="wptb-recovery-issue">
              <div className="wptb-recovery-issue-header">
                <span className="wptb-recovery-issue-icon wptb-recovery-issue-icon--row">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12h18M12 3v18" />
                  </svg>
                </span>
                <span className="wptb-recovery-issue-title">
                  {rowIssues.length} Row{rowIssues.length > 1 ? 's' : ''} with Issues
                </span>
              </div>
              <p className="wptb-recovery-issue-description">
                Rows {rowIssues.slice(0, 5).map(r => r.row + 1).join(', ')}
                {rowIssues.length > 5 ? ` and ${rowIssues.length - 5} more` : ''} have incomplete coverage.
              </p>
            </div>
          )}
        </div>

        <div className="wptb-recovery-actions">
          <button 
            className="wptb-recovery-btn wptb-recovery-btn--secondary"
            onClick={onCancel}
          >
            Go Back
          </button>
          <button 
            className="wptb-recovery-btn wptb-recovery-btn--primary"
            onClick={onRecover}
          >
            Repair Table
          </button>
        </div>
      </div>
    </div>
  );
};

export default Recovery;
